/**
 * DIFF-8 seed — quote rate samples + carrier rate tables so the 6-source
 * fusion shows data across all sources on first load.
 *
 * Existing `carrier_rate_tables` and `carrier_invoices` seeds supply two
 * of the six sources. We add a few more rows to guarantee coverage on the
 * demo lanes the screen defaults to (IL→TX, CA→NV).
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const T = DEMO_TENANT_ID;
const iso = (days = 0, hours = 0) => new Date(Date.now() - (days * 86400000 + hours * 3600000)).toISOString();

/**
 * Synthetic DIFF-8 rate samples. These get merged into the existing
 * `carrier_rate_tables` and `rate_confirmations` collections so the fusion
 * surface has real numbers to work with.
 */
export const SEED_QUOTING_RATE_TABLES = [
  { id: 'qrt-0001', tenantId: T, carrierName: 'Old Dominion Freight Line', carrierScac: 'ODFL', originState: 'IL', destState: 'TX', baseRate: 2680, active: true,
    isDeleted: false, createdAt: iso(45), updatedAt: iso(5), version: 1, source: 'admin' },
  { id: 'qrt-0002', tenantId: T, carrierName: 'XPO Logistics',             carrierScac: 'CNWY', originState: 'IL', destState: 'TX', baseRate: 2715, active: true,
    isDeleted: false, createdAt: iso(50), updatedAt: iso(8), version: 1, source: 'admin' },
  { id: 'qrt-0003', tenantId: T, carrierName: 'USA Truck',                 carrierScac: 'USAT', originState: 'CA', destState: 'NV', baseRate: 1860, active: true,
    isDeleted: false, createdAt: iso(60), updatedAt: iso(10), version: 1, source: 'admin' },
  { id: 'qrt-0004', tenantId: T, carrierName: 'Schneider',                 carrierScac: 'SCNN', originState: 'CA', destState: 'NV', baseRate: 1885, active: true,
    isDeleted: false, createdAt: iso(55), updatedAt: iso(7), version: 1, source: 'admin' },
  { id: 'qrt-0005', tenantId: T, carrierName: 'Saia LTL Freight',          carrierScac: 'SAIA', originState: 'GA', destState: 'FL', baseRate: 1280, active: true,
    isDeleted: false, createdAt: iso(40), updatedAt: iso(12), version: 1, source: 'admin' },
];

export const SEED_QUOTING_RATE_CONFIRMATIONS = [
  { id: 'qrc-0001', tenantId: T, carrierId: 'carrier-od',    agreedRate: 2770, origin: { state: 'IL' }, destination: { state: 'TX' }, signedAt: iso(6),
    isDeleted: false, createdAt: iso(6), updatedAt: iso(6), version: 1, source: 'dispatch' },
  { id: 'qrc-0002', tenantId: T, carrierId: 'carrier-xpo',   agreedRate: 2820, origin: { state: 'IL' }, destination: { state: 'TX' }, signedAt: iso(9),
    isDeleted: false, createdAt: iso(9), updatedAt: iso(9), version: 1, source: 'dispatch' },
  { id: 'qrc-0003', tenantId: T, carrierId: 'carrier-usat',  agreedRate: 1895, origin: { state: 'CA' }, destination: { state: 'NV' }, signedAt: iso(4),
    isDeleted: false, createdAt: iso(4), updatedAt: iso(4), version: 1, source: 'dispatch' },
];

export const SEED_QUOTING_INVOICES = [
  { id: 'qci-0001', tenantId: T, carrierId: 'carrier-od',   invoiceNumber: 'ODFL-99001', origin: { state: 'IL' }, destination: { state: 'TX' }, totalAmount: 2790, status: 'paid',     receivedAt: iso(10),
    isDeleted: false, createdAt: iso(10), updatedAt: iso(3), version: 2, source: 'edi' },
  { id: 'qci-0002', tenantId: T, carrierId: 'carrier-xpo',  invoiceNumber: 'XPO-88042',  origin: { state: 'IL' }, destination: { state: 'TX' }, totalAmount: 2840, status: 'approved', receivedAt: iso(15),
    isDeleted: false, createdAt: iso(15), updatedAt: iso(5), version: 2, source: 'edi' },
  { id: 'qci-0003', tenantId: T, carrierId: 'carrier-usat', invoiceNumber: 'USAT-22017', origin: { state: 'CA' }, destination: { state: 'NV' }, totalAmount: 1910, status: 'paid',     receivedAt: iso(12),
    isDeleted: false, createdAt: iso(12), updatedAt: iso(2), version: 2, source: 'edi' },
];

/**
 * Demo loads ending in IL recently — primes the carrier-hunger detection
 * (empty trailers in IL that can take the IL→TX quote cheap).
 */
export const SEED_QUOTING_HUNGER_LOADS = [
  { id: 'qhl-0001', tenantId: T, loadNumber: 'WC-11001', carrierId: 'carrier-od',   carrierName: 'Old Dominion Freight Line',
    origin: { state: 'WI', city: 'Milwaukee' }, destination: { state: 'IL', city: 'Chicago' },
    status: 'delivered', actualDeliveryDate: iso(1), financials: { customerRate: 1250, margin: 160 },
    isDeleted: false, createdAt: iso(3), updatedAt: iso(1), version: 2, source: 'ops' },
  { id: 'qhl-0002', tenantId: T, loadNumber: 'WC-11002', carrierId: 'carrier-od',   carrierName: 'Old Dominion Freight Line',
    origin: { state: 'MI', city: 'Detroit' }, destination: { state: 'IL', city: 'Chicago' },
    status: 'delivered', actualDeliveryDate: iso(2), financials: { customerRate: 1480, margin: 190 },
    isDeleted: false, createdAt: iso(4), updatedAt: iso(2), version: 2, source: 'ops' },
  { id: 'qhl-0003', tenantId: T, loadNumber: 'WC-11003', carrierId: 'carrier-xpo',  carrierName: 'XPO Logistics',
    origin: { state: 'OH', city: 'Columbus' }, destination: { state: 'IL', city: 'Rockford' },
    status: 'delivered', actualDeliveryDate: iso(1), financials: { customerRate: 1620, margin: 220 },
    isDeleted: false, createdAt: iso(3), updatedAt: iso(1), version: 2, source: 'ops' },
];

/**
 * Three demo quotes matching the 3-quote acceptance criterion.
 */
export const SEED_QUOTING_QUOTES = [
  { id: 'q-fusion-001', tenantId: T, quoteNumber: 'QT-20250',
    origin: { state: 'IL', city: 'Chicago' }, destination: { state: 'TX', city: 'Dallas' },
    customerId: 'cust-pacific-fresh', customerName: 'Pacific Fresh Foods',
    mode: 'OTR', weight: 38000, customerRate: 2850, status: 'open',
    isDeleted: false, createdAt: iso(0, 2), updatedAt: iso(0, 2), version: 1, source: 'quoting_intel' },
  { id: 'q-fusion-002', tenantId: T, quoteNumber: 'QT-20251',
    origin: { state: 'CA', city: 'Modesto' }, destination: { state: 'NV', city: 'Reno' },
    customerId: 'cust-sunrise-beverages', customerName: 'Sunrise Beverages',
    mode: 'OTR', weight: 32000, customerRate: 1750, status: 'open',
    isDeleted: false, createdAt: iso(0, 4), updatedAt: iso(0, 4), version: 1, source: 'quoting_intel' },
  { id: 'q-fusion-003', tenantId: T, quoteNumber: 'QT-20252',
    origin: { state: 'GA', city: 'Atlanta' }, destination: { state: 'FL', city: 'Miami' },
    customerId: 'cust-keyston', customerName: 'Keyston Bros',
    mode: 'LTL', weight: 9400, customerRate: 1380, status: 'open',
    isDeleted: false, createdAt: iso(0, 6), updatedAt: iso(0, 6), version: 1, source: 'quoting_intel' },
];

export const QUOTING_SEED_SUMMARY = {
  carrier_rate_tables_added: SEED_QUOTING_RATE_TABLES.length,
  rate_confirmations_added: SEED_QUOTING_RATE_CONFIRMATIONS.length,
  carrier_invoices_added: SEED_QUOTING_INVOICES.length,
  hunger_loads: SEED_QUOTING_HUNGER_LOADS.length,
  quotes: SEED_QUOTING_QUOTES.length,
};
